// frontend/src/components/Layout/Sidebar.js
import React, { createContext, useContext, useState } from 'react';
import {
    Box,
    VStack,
    Text,
    Icon,
    Flex,
    Image,
    Divider,
    Collapse,
} from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    FiHome,
    FiUsers,
    FiDollarSign,
    FiCalendar,
    FiEdit3,
    FiSettings,
    FiUser,
    FiUserPlus,
    FiBook,
    FiMessageCircle,
    FiBookOpen,
    FiChevronDown,
    FiChevronRight,
    FiTrendingDown,
    FiGitMerge,
    FiImage,
    FiBarChart2,
    FiTag,
    FiHelpCircle,
} from 'react-icons/fi';

// Sur téléphone, le menu est un tiroir : on le referme dès qu'un lien est touché.
const NavigationContext = createContext(null);

// ── Lien simple ──────────────────────────────────────────────
const SidebarItem = ({ icon, children, to, indent = false }) => {
    const location = useLocation();
    const isActive = location.pathname === to;
    const fermerMenu = useContext(NavigationContext);

    return (
        <Link to={to} style={{ textDecoration: 'none' }} onClick={fermerMenu || undefined}>
            <Flex
                align="center"
                py={2}
                px={4}
                mx={3}
                pl={indent ? 8 : 4}
                borderRadius="md"
                cursor="pointer"
                bg={isActive ? 'eecf.500' : 'transparent'}
                color={isActive ? 'white' : indent ? 'gray.500' : 'gray.600'}
                fontSize={indent ? 'sm' : 'sm'}
                _hover={{
                    bg: isActive ? 'eecf.600' : 'gray.100',
                    color: isActive ? 'white' : 'gray.900',
                }}
            >
                {icon && <Icon mr={3} fontSize="14" as={icon} />}
                {children}
            </Flex>
        </Link>
    );
};

// ── Groupe repliable ─────────────────────────────────────────
const SidebarGroup = ({ icon, label, children, paths = [] }) => {
    const location = useLocation();
    const hasActive = paths.some(p => location.pathname === p);
    const [open, setOpen] = useState(hasActive);

    return (
        <Box>
            <Flex
                align="center"
                py={2}
                px={4}
                mx={3}
                borderRadius="md"
                cursor="pointer"
                color={hasActive ? 'eecf.600' : 'gray.600'}
                fontWeight={hasActive ? '600' : '500'}
                fontSize="sm"
                _hover={{ bg: 'gray.100', color: 'gray.900' }}
                onClick={() => setOpen(o => !o)}
                userSelect="none"
            >
                {icon && <Icon mr={3} fontSize="14" as={icon} />}
                <Text flex={1}>{label}</Text>
                <Icon as={open ? FiChevronDown : FiChevronRight} fontSize="12" color="gray.400" />
            </Flex>
            <Collapse in={open} animateOpacity>
                <VStack spacing={0} align="stretch" mb={1}>
                    {children}
                </VStack>
            </Collapse>
        </Box>
    );
};

// ── Sidebar principale ───────────────────────────────────────
const Sidebar = ({ onNavigate }) => {
    const { user } = useAuth();

    const getRoleLabel = () => {
        if (!user) return 'EECF';
        if (user.is_admin) return 'Admin';
        const labels = { admin: 'Admin', pasteur: 'Pasteur', president: 'Président', commissaire: 'Commissaire', tresorier: 'Trésorier', secretaire: 'Secrétaire', editeur: 'Éditeur', membre: 'Membre' };
        return labels[user.role] || 'Membre';
    };

    const isAdmin   = user?.role === 'admin' || user?.is_admin;
    const isPasteur = user?.role === 'pasteur';
    const isPresident = user?.role === 'president';
    const isTresorier = user?.role === 'tresorier';
    const isSecretaire = user?.role === 'secretaire';
    const isCommissaire = user?.role === 'commissaire';
    const isEditeur = user?.role === 'editeur';

    // Sections communes à tous
    const renderBase = () => (
        <>
            <SidebarItem icon={FiHome} to="/dashboard">Tableau de bord</SidebarItem>
            <SidebarItem icon={FiUser} to="/profile">Mon Profil</SidebarItem>
            <SidebarItem icon={FiDollarSign} to="/contributions/me">Mes cotisations</SidebarItem>
            <SidebarItem icon={FiImage} to="/annuaire">Annuaire</SidebarItem>
            <SidebarItem icon={FiEdit3} to="/blog">Blog</SidebarItem>
            <SidebarItem icon={FiBook} to="/bible">Bible interactive</SidebarItem>
            <SidebarItem icon={FiMessageCircle} to="/contact-pasteur">Contacter le pasteur</SidebarItem>
            <SidebarItem icon={FiHelpCircle} to="/aide">Aide</SidebarItem>
        </>
    );

    // Groupe Membres
    const renderGroupMembres = () => (
        <SidebarGroup icon={FiUsers} label="Membres" paths={['/members', '/admin/enrollment-validation']}>
            <SidebarItem icon={FiUsers} to="/members" indent>Gestion des membres</SidebarItem>
            <SidebarItem icon={FiGitMerge} to="/members/doublons" indent>Fusion doublons</SidebarItem>
            <SidebarItem icon={FiUserPlus} to="/admin/enrollment-validation" indent>Demandes d'adhésion</SidebarItem>
        </SidebarGroup>
    );

    // Groupe Finances — admin/pasteur
    const renderGroupFinancesPlein = (avecImport = false) => (
        <SidebarGroup icon={FiDollarSign} label="Finances" paths={['/contributions', '/expenses', '/comptabilite', '/contributions/import']}>
            <SidebarItem icon={FiDollarSign} to="/contributions" indent>Gestion des cotisations</SidebarItem>
            <SidebarItem icon={FiTrendingDown} to="/expenses" indent>Dépenses</SidebarItem>
            <SidebarItem icon={FiBookOpen} to="/comptabilite" indent>Comptabilité</SidebarItem>
            {avecImport && <SidebarItem icon={FiDollarSign} to="/contributions/import" indent>Import paiements historiques</SidebarItem>}
        </SidebarGroup>
    );

    // Groupe Finances — trésorier (même items)
    const renderGroupFinancesTresorier = () => (
        <SidebarGroup icon={FiDollarSign} label="Finances" paths={['/contributions', '/expenses', '/comptabilite']}>
            <SidebarItem icon={FiDollarSign} to="/contributions" indent>Gestion des cotisations</SidebarItem>
            <SidebarItem icon={FiTrendingDown} to="/expenses" indent>Dépenses</SidebarItem>
            <SidebarItem icon={FiBookOpen} to="/comptabilite" indent>Comptabilité</SidebarItem>
        </SidebarGroup>
    );

    // Groupe Finances — secrétaire (cotisations seulement)
    const renderGroupFinancesSecretaire = () => (
        <SidebarGroup icon={FiDollarSign} label="Finances" paths={['/contributions']}>
            <SidebarItem icon={FiDollarSign} to="/contributions" indent>Gestion des cotisations</SidebarItem>
        </SidebarGroup>
    );

    // Groupe Culte
    const renderGroupCulte = () => (
        <SidebarGroup icon={FiCalendar} label="Culte" paths={['/worship', '/admin/biblical-reading']}>
            <SidebarItem icon={FiCalendar} to="/worship" indent>Gestion du culte</SidebarItem>
            <SidebarItem icon={FiBook} to="/admin/biblical-reading" indent>Lecture Biblique</SidebarItem>
        </SidebarGroup>
    );

    // Groupe Communication
    const renderGroupCommunication = () => (
        <SidebarGroup icon={FiMessageCircle} label="Communication" paths={['/admin/blog', '/admin/blog-categories', '/admin/whatsapp']}>
            <SidebarItem icon={FiEdit3} to="/admin/blog" indent>Gestion du Blog</SidebarItem>
            <SidebarItem icon={FiTag} to="/admin/blog-categories" indent>Catégories du Blog</SidebarItem>
            <SidebarItem icon={FiMessageCircle} to="/admin/whatsapp" indent>WhatsApp</SidebarItem>
        </SidebarGroup>
    );

    return (
        <NavigationContext.Provider value={onNavigate || null}>
        <Box
            w="250px"
            bg="white"
            borderRight="1px"
            borderRightColor="gray.200"
            height="100vh"
            position="fixed"
            overflowY="auto"
        >
            {/* En-tête */}
            <Flex p="5" align="center" justify="center" direction="column">
                <Image
                    src="/logo-eecf.png"
                    alt="EECF Logo"
                    width="56px"
                    height="56px"
                    fallbackSrc="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMjQiIHN0cm9rZT0iIzFGNzA0MCIgc3Ryb2tlLXdpZHRoPSIzIiBmaWxsPSIjMkRDNjUzIi8+Cjx0ZXh0IHg9IjMwIiB5PSIzNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0id2hpdGUiIGZvbnQtc2l6ZT0iMTAiIGZvbnQtd2VpZ2h0PSJib2xkIj5FRUNGPC90ZXh0Pgo8L3N2Zz4K"
                    mb={2}
                />
                <Text fontSize="md" fontWeight="bold" color="eecf.500">{getRoleLabel()} EECF</Text>
                <Text fontSize="xs" color="gray.400" textAlign="center">Interface d'administration</Text>
            </Flex>

            <Divider />

            <VStack spacing={0} align="stretch" mt={2} pb={4}>
                {/* Base — tous les rôles */}
                {renderBase()}

                <Divider my={2} />

                {/* Admin */}
                {isAdmin && (
                    <>
                        {renderGroupMembres()}
                        {renderGroupFinancesPlein(true)}
                        {renderGroupCulte()}
                        {renderGroupCommunication()}
                        <SidebarItem icon={FiMessageCircle} to="/admin/messages-pasteur">Messages des membres</SidebarItem>
                        <SidebarItem icon={FiBarChart2} to="/admin/statistiques">Statistiques</SidebarItem>
                        <SidebarItem icon={FiUsers} to="/admin/roles">Rôles & Permissions</SidebarItem>
                        <SidebarItem icon={FiSettings} to="/settings">Paramètres</SidebarItem>
                    </>
                )}

                {/* Éditeur */}
                {!isAdmin && isEditeur && (
                    <SidebarItem icon={FiEdit3} to="/admin/blog">Gestion du Blog</SidebarItem>
                )}

                {/* Pasteur et président (les messages privés des membres sont réservés au pasteur) */}
                {(isPasteur || isPresident) && (
                    <>
                        {renderGroupMembres()}
                        {renderGroupFinancesPlein()}
                        {renderGroupCulte()}
                        {renderGroupCommunication()}
                        {isPasteur && <SidebarItem icon={FiMessageCircle} to="/admin/messages-pasteur">Messages des membres</SidebarItem>}
                    </>
                )}

                {/* Trésorier */}
                {isTresorier && renderGroupFinancesTresorier()}

                {/* Secrétaire */}
                {isSecretaire && renderGroupFinancesSecretaire()}

                {/* Commissaire aux comptes : lecture des finances et rapprochement bancaire */}
                {isCommissaire && (
                    <>
                        {renderGroupFinancesTresorier()}
                        <SidebarItem icon={FiCalendar} to="/worship">Culte et offrandes</SidebarItem>
                    </>
                )}
            </VStack>
        </Box>
        </NavigationContext.Provider>
    );
};

export default Sidebar;
